import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  AppBar,
  Toolbar,
  InputBase,
  Snackbar,
  Alert,
  styled,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Collapse,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  ArrowUp,
  ArrowLeft,
  MoreHorizontal,
  Check,
  CheckCheck,
} from 'lucide-react';
import chatService from '../../services/AGTUps-product-services/chatService';

// Importar componentes de negociación automática
import AutoNegotiationControl from './AutoNegotiationControl.js';
import AutoNegotiationManager from './AutoNegotiationManager.js';

// ====================================
// OBTENER USUARIO AUTENTICADO DESDE LOCALSTORAGE
// ====================================
const firebaseAuthKey =
  'firebase:authUser:AIzaSyCl_QY3Y0RYRNuMc541mwhBlMW2VbLiqhQ:[DEFAULT]';
const storedUser = localStorage.getItem(firebaseAuthKey);
const firebaseUser = storedUser ? JSON.parse(storedUser) : null;
const localUserId = firebaseUser?.uid || '';

// ====================================
// ESTILOS PERSONALIZADOS
// ====================================
const ChatContainer = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    height: 'calc(100vh - 240px)',
    maxWidth: '1200px',
    width: '90%',
    margin: '0 auto',
    marginBottom: theme.spacing(2),
    display: 'flex',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : theme.palette.mode === 'dark' ? '#272729' : '#F2F2F7',
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    borderRadius: theme.spacing(3),
    overflow: 'hidden',
    position: 'relative',
    '&::-webkit-scrollbar': { width: '8px' },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0, 0, 0, 0.2)',
      borderRadius: '4px',
    },
  };
});

const ChatListContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ theme, isMobile }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: isMobile ? '100%' : '30%',
    minWidth: isMobile ? '100%' : '280px',
    maxWidth: isMobile ? '100%' : '350px',
    height: '100%',
    borderRight: `1px solid ${
      theme.palette.mode === 'dark' ? '#464649' : '#C7C7CC'
    }`,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : theme.palette.mode === 'dark' ? '#272729' : '#F2F2F7',
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    overflow: 'hidden',
  };
});

const ChatArea = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    flex: 1,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.2)'
      : theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    overflow: 'hidden',
  };
});

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSender',
})(({ theme, isSender }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    maxWidth: '70%',
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.spacing(2),
    backgroundColor: isSender
      ? (isWallpaperMode ? 'rgba(128, 128, 128, 0.35)' : '#0385FF')
      : isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : theme.palette.mode === 'dark'
      ? '#464649'
      : '#F2F2F7',
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    color: isSender
      ? '#FFFFFF'
      : theme.palette.text.primary,
    alignSelf: isSender ? 'flex-end' : 'flex-start',
    margin: theme.spacing(1),
    position: 'relative',
  };
});

const SearchInput = styled(InputBase)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : theme.palette.mode === 'dark' ? '#464649' : '#F2F2F7',
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    borderRadius: theme.spacing(3),
    padding: theme.spacing(1.5, 2),
    width: 'calc(100% - 32px)',
    margin: theme.spacing(1, 2),
    '&.MuiInputBase-root': {
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.25)'
        : theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
      backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
      WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
      border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    },
  };
});

const MessageInput = styled(InputBase)(({ theme }) => ({
  '& .MuiInputBase-input': {
    padding: theme.spacing(1.75, 2),
    lineHeight: '1.2',
    fontSize: '0.9rem',
  },
  '&.Mui-disabled': {
    opacity: 0.6,
  },
}));

const ChatHeader = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isListHeader',
})(({ theme, isListHeader }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'static',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : theme.palette.mode === 'dark' ? '#272729' : '#F2F2F7',
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
    boxShadow: 'none',
    borderTopLeftRadius: isListHeader ? theme.spacing(3) : 0,
    borderTopRightRadius: !isListHeader ? theme.spacing(3) : 0,
    '& .MuiToolbar-root': {
      padding: theme.spacing(2),
      minHeight: 'auto',
    },
  };
});

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: theme.spacing(2),
  margin: theme.spacing(0.5, 1),
  width: 'auto',
  minWidth: 0,
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.05)',
  },
  '&.Mui-selected': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)',
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.15)'
          : 'rgba(0, 0, 0, 0.15)',
    },
  },
}));

// ====================================
// COMPONENTE: ENHANCED MESSAGE INPUT
// ====================================
const EnhancedMessageInput = ({ onSendMessage, onAIReply, disabled = false }) => {
  const theme = useTheme();
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const [message, setMessage] = useState('');
  const [isAIMode, setIsAIMode] = useState(false);
  const [showAIOptions, setShowAIOptions] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const aiSuggestions = [
    {
      text: 'Negociar mejor precio',
      icon: '💰',
      description: 'Obtener una mejor oferta',
    },
    {
      text: 'Solicitar más información',
      icon: '📋',
      description: 'Detalles del producto',
    },
    {
      text: 'Proponer alternativa',
      icon: '🔄',
      description: 'Sugerir opciones',
    },
    {
      text: 'Cerrar trato',
      icon: '🤝',
      description: 'Finalizar negociación',
    },
  ];

  const handleSend = async () => {
    if (!message.trim() || isProcessing) return;

    setIsProcessing(true);
    try {
      if (isAIMode) {
        await onAIReply(message);
      } else {
        await onSendMessage(message);
      }
      setMessage('');
      setIsAIMode(false);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Box sx={{ width: '100%', mb: showAIOptions ? 2 : 0 }}>
      <Box
        sx={{
          position: 'relative',
          '& .MuiInputBase-root': {
            pr: '100px',
          },
        }}
      >
        <MessageInput
          fullWidth
          placeholder={
            isAIMode ? '¿Cómo quieres que responda la IA?' : 'Escribe un mensaje...'
          }
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
          disabled={disabled || isProcessing}
          multiline
          maxRows={4}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: isAIMode ? 'rgba(147, 51, 234, 0.1)' : 'inherit',
              transition: 'background-color 0.2s',
            },
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            gap: 1,
          }}
        >
          <IconButton
            onClick={() => setShowAIOptions(!showAIOptions)}
            sx={{
              backgroundColor: isAIMode ? 'rgba(147, 51, 234, 0.2)' : 'transparent',
              '&:hover': {
                backgroundColor: isAIMode
                  ? 'rgba(147, 51, 234, 0.3)'
                  : 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke={isAIMode ? '#9333EA' : 'currentColor'}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
              <path d="M5 3v4" />
              <path d="M19 17v4" />
              <path d="M3 5h4" />
              <path d="M17 19h4" />
            </svg>
          </IconButton>

          <IconButton
            onClick={handleSend}
            disabled={!message.trim() || isProcessing}
            sx={{
              backgroundColor: message.trim()
                ? (isWallpaperMode ? '#FFFFFF' : '#0385FF')
                : 'transparent',
              '&:hover': {
                backgroundColor: message.trim()
                  ? (isWallpaperMode ? '#FFFFFF' : '#0374e4')
                  : 'transparent',
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.12)',
              },
            }}
          >
            {isProcessing ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <ArrowUp
                size={20}
                color={
                  !message.trim()
                    ? '#9E9E9E'
                    : isWallpaperMode
                    ? '#000000'
                    : '#FFFFFF'
                }
              />
            )}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={showAIOptions}>
        <Paper
          elevation={0}
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 3,
            border: (theme) => {
              const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
              return isWallpaperMode
                ? '1px solid rgba(128, 128, 128, 0.3)'
                : `1px solid ${theme.palette.divider}`;
            },
            backgroundColor: (theme) => {
              const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
              return isWallpaperMode
                ? 'rgba(128, 128, 128, 0.25)'
                : theme.palette.mode === 'dark' ? '#1E1E1E' : '#FFFFFF';
            },
            backdropFilter: (theme) => {
              const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
              return isWallpaperMode ? 'blur(8px)' : 'none';
            },
            WebkitBackdropFilter: (theme) => {
              const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
              return isWallpaperMode ? 'blur(8px)' : 'none';
            },
            color: (theme) => (theme.palette.mode === 'dark' ? '#fff' : '#000'),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="subtitle2">Sugerencias de IA</Typography>
            <IconButton
              size="small"
              onClick={() => setShowAIOptions(false)}
              sx={{
                color: (theme) => (theme.palette.mode === 'dark' ? '#fff' : '#000'),
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </IconButton>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mb: 2,
            }}
          >
            {aiSuggestions.map((suggestion) => (
              <Button
                key={suggestion.text}
                variant="outlined"
                size="small"
                startIcon={<span>{suggestion.icon}</span>}
                title={suggestion.description}
                onClick={() => {
                  setMessage(suggestion.text);
                  setIsAIMode(true);
                  setShowAIOptions(false);
                }}
                sx={(theme) => {
                  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
                  return {
                    color: isWallpaperMode
                      ? '#FFFFFF'
                      : (theme.palette.mode === 'dark' ? '#fff' : '#000'),
                    borderRadius: '20px',
                    textTransform: 'none',
                    borderColor: isWallpaperMode ? '#FFFFFF' : 'rgba(0,0,0,0.12)',
                    '&:hover': {
                      borderColor: isWallpaperMode ? '#FFFFFF' : 'rgba(0,0,0,0.24)',
                      backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.1)' : undefined,
                    },
                  };
                }}
              >
                {suggestion.text}
              </Button>
            ))}
          </Box>

          <Typography
            variant="caption"
            sx={{
              color: (theme) => (theme.palette.mode === 'dark' ? '#fff' : '#000'),
            }}
          >
            La IA tendrá en cuenta el contexto y el historial de la conversación
          </Typography>
        </Paper>
      </Collapse>
    </Box>
  );
};

// ====================================
// COMPONENTE PRINCIPAL: ACTIVE
// ====================================
const Active = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
 
  const [conversations, setConversations] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  // Eliminar setIsTyping que no se usa, pero mantener isTyping
  const [isTyping] = useState(false);
  const [loadingStates, setLoadingStates] = useState({
    chats: false,
    messages: false,
    sending: false,
    aiReply: false,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error',
  });
  const [anchorElChatList, setAnchorElChatList] = useState(null);
  const [anchorElChatArea, setAnchorElChatArea] = useState(null);
  const [openDeleteAllDialog, setOpenDeleteAllDialog] = useState(false);
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState('');
  const [openDeleteOneDialog, setOpenDeleteOneDialog] = useState(false);
  const messagesEndRef = useRef(null);
 
  // Estado para negociación automática
  const [autoNegotiation, setAutoNegotiation] = useState({
    active: false,
    processing: false,
    responseCount: 0,
    lastResponseTime: null,
  });
 
  // Envolver showSnackbar en useCallback
  const showSnackbar = useCallback((message, severity = 'error') => {
    setSnackbar({ open: true, message, severity });
  }, []);
 
  // Envolver handleError en useCallback
  const handleError = useCallback((error, action = '') => {
    console.error(`Error in ${action}:`, error);
    let errorMessage = 'Ha ocurrido un error';
    if (error.response?.status === 401) {
      errorMessage = 'Sesión expirada. Por favor, vuelve a iniciar sesión.';
    } else if (error.response?.status === 404) {
      errorMessage = 'No se encontró el recurso solicitado.';
    } else if (error.message) {
      errorMessage = error.message;
    }
    showSnackbar(errorMessage, 'error');
  }, [showSnackbar]);
 
  // Envolver fetchChats en su propio useCallback
  const fetchChats = useCallback(async () => {
    setLoadingStates((prev) => ({ ...prev, chats: true }));
    try {
      const response = await chatService.getUserChats();
      if (response.success && response.data) {
        setConversations(response.data);
      }
    } catch (error) {
      handleError(error, 'fetchChats');
    } finally {
      setLoadingStates((prev) => ({ ...prev, chats: false }));
    }
  }, [handleError]);

  const fetchMessages = async (chatId) => {
    setLoadingStates((prev) => ({ ...prev, messages: true }));
    try {
      const response = await chatService.getConversation(chatId);
      if (response.success && response.data) {
        setMessages(response.data);
      }
    } catch (error) {
      handleError(error, 'fetchMessages');
    } finally {
      setLoadingStates((prev) => ({ ...prev, messages: false }));
    }
  };

  const handleMarkAsRead = async (chatId) => {
    try {
      const response = await chatService.markChatAsRead(chatId);
      if (response.success) {
        setConversations((prev) =>
          prev.map((conv) =>
            conv.chatId === chatId ? { ...conv, unreadCount: 0 } : conv
          )
        );
      }
    } catch (error) {
      handleError(error, 'handleMarkAsRead');
    }
  };

  const handleEnhancedMessage = async (messageText) => {
    if (!messageText.trim() || !selectedChat) return;
    setLoadingStates((prev) => ({ ...prev, sending: true }));

    try {
      const messageData = {
        chatId: selectedChat.chatId,
        senderId: localUserId,
        receiverId: selectedChat.otherUserId,
        message: messageText.trim(),
        productName: selectedChat.productName,
        productImage: selectedChat.productImage,
        otherCompanyName: selectedChat.otherCompanyName,
      };

      const response = await chatService.sendMessage(messageData);
      if (response.success) {
        setMessages((prev) => [...prev, response.data]);
        fetchChats();
      }
    } catch (error) {
      handleError(error, 'handleEnhancedMessage');
    } finally {
      setLoadingStates((prev) => ({ ...prev, sending: false }));
    }
  };

  const handleEnhancedAIReply = async (prompt) => {
    if (!selectedChat) return;
    setLoadingStates((prev) => ({ ...prev, aiReply: true }));

    try {
      const response = await chatService.replyWithAI(selectedChat.chatId, prompt);
      if (response.success) {
        showSnackbar('La IA respondió con éxito', 'success');
        if (response.data?.chatMessage) {
          setMessages((prev) => [...prev, response.data.chatMessage]);
        }
        fetchChats();
      }
    } catch (error) {
      handleError(error, 'handleEnhancedAIReply');
    } finally {
      setLoadingStates((prev) => ({ ...prev, aiReply: false }));
    }
  };

  // Función para manejar mensajes entrantes y ejecutar negociación automática
  const handleIncomingMessage = useCallback(async (incomingMessage) => {
    if (!autoNegotiation.active || autoNegotiation.processing || !selectedChat) return;
   
    setAutoNegotiation(prev => ({ 
      ...prev, 
      processing: true 
    }));
   
    try {
      const response = await chatService.replyWithAI(
        selectedChat.chatId,
        "Responde a este mensaje como mi representante en la negociación: " + incomingMessage.message
      );
   
      if (response.success && response.data?.chatMessage) {
        setMessages(prev => [...prev, response.data.chatMessage]);
        setAutoNegotiation(prev => ({
          ...prev,
          responseCount: prev.responseCount + 1,
          lastResponseTime: new Date().toISOString()
        }));
        fetchChats();
      }
    } catch (error) {
      console.error('Error en negociación automática:', error);
      if (error.message.includes('API limit') || error.message.includes('unauthorized')) {
        setAutoNegotiation(prev => ({
          ...prev,
          active: false,
          processing: false
        }));
        showSnackbar('Se desactivó la negociación automática debido a un error', 'error');
      }
    } finally {
      setAutoNegotiation(prev => ({ 
        ...prev, 
        processing: false 
      }));
    }
  }, [autoNegotiation.active, autoNegotiation.processing, selectedChat, fetchChats, showSnackbar]);

  const handleChatSelection = async (chat) => {
    setSelectedChat(chat);
    await fetchMessages(chat.chatId);
    if (chat.unreadCount > 0) {
      await handleMarkAsRead(chat.chatId);
    }
  };

  const handleOpenChatListMenu = (event) => {
    setAnchorElChatList(event.currentTarget);
  };
  const handleCloseChatListMenu = () => {
    setAnchorElChatList(null);
  };

  const handleOpenChatAreaMenu = (event) => {
    setAnchorElChatArea(event.currentTarget);
  };
  const handleCloseChatAreaMenu = () => {
    setAnchorElChatArea(null);
  };

  const handleDeleteChat = async () => {
    if (!selectedChat) return;
    try {
      const response = await chatService.deleteChat(selectedChat.chatId);
      if (response.success) {
        showSnackbar('Chat borrado correctamente', 'success');
        setSelectedChat(null);
        fetchChats();
      }
    } catch (error) {
      handleError(error, 'handleDeleteChat');
    }
  };

  const handleDeleteAllChats = async () => {
    try {
      const chatIds = conversations.map((c) => c.chatId);
      await Promise.all(chatIds.map((id) => chatService.deleteChat(id)));
      setSelectedChat(null);
      setConversations([]);
      showSnackbar('Se borraron todos los chats', 'success');
    } catch (error) {
      handleError(error, 'handleDeleteAllChats');
    }
  };

  const renderMessageGroups = useCallback(() => {
    let currentDate = null;

    return messages.map((message, index) => {
      const messageDate = new Date(message.timestamp).toLocaleDateString();
      const showDateSeparator = messageDate !== currentDate;
      currentDate = messageDate;

      const isSender = message.senderId === localUserId;

      return (
        <React.Fragment key={`${message.timestamp}-${index}`}>
          {showDateSeparator && (
            <Typography
              align="center"
              variant="caption"
              sx={{
                backgroundColor: 'rgba(0,0,0,0.1)',
                borderRadius: '12px',
                padding: '4px 12px',
                margin: '8px auto',
                display: 'inline-block',
              }}
            >
              {messageDate}
            </Typography>
          )}

          <MessageBubble isSender={isSender}>
            <Typography variant="body1">
              {message.message}
              {message.aiGenerated && ' (IA)'}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                mt: 0.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: isSender
                    ? '#FFFFFF'
                    : theme.palette.mode === 'dark'
                    ? '#FFFFFF'
                    : '#000',
                  mr: 1,
                }}
              >
                {new Date(message.timestamp).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Typography>
              {isSender && (
                <>
                  {message.status === 'sent' && (
                    <Check size={16} color="#9E9E9E" />
                  )}
                  {(message.status === 'delivered' || message.status === 'read') && (
                    <CheckCheck size={16} color="#9E9E9E" />
                  )}
                </>
              )}
            </Box>
          </MessageBubble>
        </React.Fragment>
      );
    });
  }, [messages, theme.palette.mode]);

  const filteredChats = useMemo(() => {
    return conversations.filter((chat) =>
      (chat.otherCompanyName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (chat.lastMessage?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    );
  }, [conversations, searchTerm]);

  const memoizedMessageGroups = useMemo(() => renderMessageGroups(), [renderMessageGroups]);

  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      // Si el último mensaje NO es nuestro y la negociación automática está activa, se delega a AutoNegotiationManager
      if (
        lastMessage.senderId !== localUserId && 
        autoNegotiation.active &&
        !lastMessage.aiGenerated
      ) {
        handleIncomingMessage(lastMessage);
      }
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, autoNegotiation.active, handleIncomingMessage]);

  useEffect(() => {
    fetchChats();
  }, [fetchChats]); // Añadir fetchChats como dependencia

  const renderChatList = () => (
    <ChatListContainer isMobile={isMobile}>
      <ChatHeader isListHeader={true}>
        <Toolbar sx={{ px: 2 }}>
          <Typography variant="h6" noWrap>
            Chats
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton color="inherit" onClick={handleOpenChatListMenu}>
            <MoreHorizontal />
          </IconButton>
          <Menu
            anchorEl={anchorElChatList}
            open={Boolean(anchorElChatList)}
            onClose={handleCloseChatListMenu}
          >
            <MenuItem
              onClick={() => {
                handleCloseChatListMenu();
                setOpenDeleteAllDialog(true);
              }}
            >
              Borrar todos los chats
            </MenuItem>
          </Menu>
        </Toolbar>
      </ChatHeader>

      <Box>
        <SearchInput
          placeholder="Buscar chat..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          startAdornment={<Search size={20} style={{ marginRight: '8px' }} />}
        />
      </Box>

      {loadingStates.chats && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      <List sx={{ flex: 1, overflow: 'auto', height: 0, px: 1 }}>
        {filteredChats.map((chat) => (
          <StyledListItem
            key={chat.chatId}
            button
            onClick={() => handleChatSelection(chat)}
            selected={selectedChat?.chatId === chat.chatId}
          >
            <ListItemAvatar sx={{ minWidth: '40px' }}>
              <Avatar src={chat.productImage} sx={{ width: 32, height: 32 }} />
            </ListItemAvatar>
            <ListItemText
              primary={
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: chat.unreadCount > 0 ? 600 : 400,
                      fontSize: '0.9rem',
                    }}
                  >
                    {chat.otherCompanyName}
                  </Typography>
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: '0.75rem',
                    }}
                  >
                    {new Date(chat.lastMessageTimestamp).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Typography>
                </Box>
              }
              secondary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    component="span"
                    variant="body2"
                    sx={(theme) => ({
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                      color: chat.unreadCount > 0
                        ? (theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF')
                        : 'inherit',
                      fontSize: '0.8rem',
                    })}
                  >
                    {chat.lastMessage}
                  </Typography>
                  {chat.unreadCount > 0 && (
                    <Box
                      sx={(theme) => ({
                        backgroundColor: theme.palette.actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.3)' : '#0385FF',
                        color: '#FFFFFF',
                        backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
                        WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
                        border: theme.palette.actualMode === 'wallpaper' ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
                        borderRadius: '50%',
                        minWidth: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        marginLeft: 'auto',
                      })}
                    >
                      {chat.unreadCount}
                    </Box>
                  )}
                </Box>
              }
              sx={{ margin: 0 }}
            />
          </StyledListItem>
        ))}
      </List>
    </ChatListContainer>
  );

  const renderChatView = () => (
    <ChatArea>
      <ChatHeader isListHeader={false}>
        <Toolbar>
          {isMobile && (
            <IconButton edge="start" color="inherit" onClick={() => setSelectedChat(null)}>
              <ArrowLeft />
            </IconButton>
          )}
          <Avatar src={selectedChat?.productImage} sx={{ mr: 2 }} />
          <Box>
            <Typography variant="h6" noWrap>
              {selectedChat?.otherCompanyName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {selectedChat?.productName}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton color="inherit">
            <Search />
          </IconButton>
          <IconButton color="inherit" onClick={handleOpenChatAreaMenu}>
            <MoreHorizontal />
          </IconButton>
          <Menu
            anchorEl={anchorElChatArea}
            open={Boolean(anchorElChatArea)}
            onClose={handleCloseChatAreaMenu}
          >
            <MenuItem
              onClick={() => {
                handleCloseChatAreaMenu();
                setOpenDeleteOneDialog(true);
              }}
            >
              Eliminar chat actual
            </MenuItem>
          </Menu>
        </Toolbar>
      </ChatHeader>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          height: 0,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {loadingStates.messages && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!loadingStates.messages && memoizedMessageGroups}

        {isTyping && (
          <MessageBubble isSender={false}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">Escribiendo...</Typography>
              <CircularProgress size={12} />
            </Box>
          </MessageBubble>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Integración del AutoNegotiationControl, AutoNegotiationManager y EnhancedMessageInput */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <AutoNegotiationControl
          isActive={autoNegotiation.active}
          isProcessing={autoNegotiation.processing}
          onToggle={() =>
            setAutoNegotiation((prev) => ({
              ...prev,
              active: !prev.active,
            }))
          }
          disabled={!selectedChat}
          responseCount={autoNegotiation.responseCount}
          lastResponseTime={autoNegotiation.lastResponseTime}
        />
        {selectedChat && (
          <AutoNegotiationManager
            isActive={autoNegotiation.active}
            chatId={selectedChat.chatId}
            lastMessageTimestamp={messages[messages.length - 1]?.timestamp}
            onNewMessage={handleIncomingMessage}
            chatService={chatService}
          />
        )}
        <EnhancedMessageInput
          onSendMessage={handleEnhancedMessage}
          onAIReply={handleEnhancedAIReply}
          disabled={!selectedChat || autoNegotiation.processing}
        />
      </Box>
    </ChatArea>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <ChatContainer>
        {isMobile ? (
          selectedChat ? (
            renderChatView()
          ) : (
            renderChatList()
          )
        ) : (
          <>
            {renderChatList()}
            {selectedChat ? (
              renderChatView()
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Selecciona un chat para comenzar
                </Typography>
              </Box>
            )}
          </>
        )}
      </ChatContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={openDeleteAllDialog}
        onClose={() => setOpenDeleteAllDialog(false)}
        sx={(theme) => {
          const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
          return {
            '& .MuiDialog-paper': {
              backgroundColor: isWallpaperMode
                ? 'rgba(128, 128, 128, 0.3)'
                : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#FFFFFF'),
              backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
              WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
              border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
            },
          };
        }}
      >
        <DialogTitle>¿Borrar todos los chats?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Esta acción borrará todos tus chats para ti (soft-delete). Para confirmar, escribe la palabra <strong>BORRAR</strong>.
          </Typography>
          <InputBase
            fullWidth
            style={{
              padding: '8px 12px',
              border: '1px solid rgba(0, 0, 0, 0.23)',
              borderRadius: 4,
            }}
            placeholder="Escribe BORRAR para confirmar"
            value={deleteAllConfirmText}
            onChange={(e) => setDeleteAllConfirmText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteAllDialog(false)}>Cancelar</Button>
          <Button
            color="error"
            disabled={deleteAllConfirmText !== 'BORRAR'}
            onClick={() => {
              setOpenDeleteAllDialog(false);
              setDeleteAllConfirmText('');
              handleDeleteAllChats();
            }}
          >
            Borrar todos
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteOneDialog}
        onClose={() => setOpenDeleteOneDialog(false)}
        sx={(theme) => {
          const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
          return {
            '& .MuiDialog-paper': {
              backgroundColor: isWallpaperMode
                ? 'rgba(128, 128, 128, 0.3)'
                : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#FFFFFF'),
              backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
              WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
              border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
            },
          };
        }}
      >
        <DialogTitle>Eliminar chat</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            ¿Estás seguro de que deseas eliminar este chat?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteOneDialog(false)}>Cancelar</Button>
          <Button
            color="error"
            onClick={() => {
              setOpenDeleteOneDialog(false);
              handleDeleteChat();
            }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Active;